/**
 * A/Bテストの群分けユーティリティ。
 *
 * 実験名と user_id から決定的にバケット（0〜99）を算出する。割当を保存しないので、
 * 再インストールや機種変更をしても同じ群に落ちる。
 *
 * 実験名をソルトに混ぜているのは、別の実験と群が完全に一致して効果が混ざるのを防ぐため。
 *
 * 集計時は BigQuery 側で同じ式を書けば群を復元できる。ログに群を残す必要はない。
 *
 * ```sql
 * MOD(
 *   CAST(CONCAT('0x', SUBSTR(TO_HEX(SHA1(CONCAT('<実験名>:', user_id))), 1, 8)) AS INT64),
 *   100
 * )
 * ```
 *
 * ★★式がずれると群がまるごと入れ替わり、しかも画面もダッシュボードも普通に描画される
 *   ため気付けない。変えるときは BigQuery 側（jambo-ds-effect-dashboard の
 *   `MSG_PREF_GROUP`）と必ず同時に変える。
 *
 * ★サーバー側の既存実験（jambo-commons の `AttackMailReplyRateExperiment`）は
 *   FARM_FINGERPRINT を使っているが、ブラウザで同じ値を出せないのでこちらは SHA-1。
 *   ソルトを `実験名 + ":"` にする点だけ揃えている。
 */
import { sha1 } from "~/lib/crypto";
import { captureError } from "~/lib/sentry/captureError";

/** バケットの総数。割合をそのままパーセントで書けるよう100にしている */
const BUCKET_COUNT = 100;

/** ハッシュの先頭から何桁を数値化するか。8桁=32bitで Number の安全整数に収まる */
const HEX_LENGTH = 8;

/** 既定の処置群の割合（パーセント） */
const DEFAULT_TREATMENT_RATIO = 50;

/**
 * 算出済みバケットのメモ化。会話一覧は数秒ごとにポーリングされるため、
 * 同じ実験・同じユーザーでハッシュを計算し直さない
 */
const bucketCache = new Map<string, number>();

/**
 * 実験名と user_id から 0〜99 のバケットを返す。
 *
 * @param experiment 実験名（`~/constants/experiments` の定数を渡す）
 * @param userId 対象ユーザーのID
 */
export const getExperimentBucket = async (
  experiment: string,
  userId: string,
): Promise<number> => {
  const cacheKey = `${experiment}:${userId}`;
  const cached = bucketCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const digest = await sha1(`${experiment}:${userId}`);
  const bucket =
    Number.parseInt(digest.slice(0, HEX_LENGTH), 16) % BUCKET_COUNT;
  bucketCache.set(cacheKey, bucket);
  return bucket;
};

/**
 * 処置群（B群）かどうかを返す。
 *
 * ハッシュの算出に失敗した場合は対照群（A群）として扱う。実験のために
 * 既存の挙動を壊さないことを優先する。
 *
 * @param experiment 実験名
 * @param userId 対象ユーザーのID
 * @param treatmentRatio 処置群に入れる割合（パーセント）。既定は50
 */
export const isInTreatmentGroup = async (
  experiment: string,
  userId: string,
  treatmentRatio: number = DEFAULT_TREATMENT_RATIO,
): Promise<boolean> => {
  try {
    const bucket = await getExperimentBucket(experiment, userId);
    return bucket < treatmentRatio;
  } catch (error) {
    captureError(error, "abTest: バケットの算出に失敗", { experiment });
    return false;
  }
};

/** テスト用: メモ化をリセットする */
export const resetExperimentBucketCache = (): void => {
  bucketCache.clear();
};
